import get from 'lodash/get';
import { formatProductLineFieldName } from './product-lines.utils';
import type { ProductLineSchema, ProductLineMetadataFieldSchema } from './product-line-schema.types';

export const PRODUCT_LINE_FILTER_KIND = 'productLine';

interface FilterMetadataField {
    Type: string;
    DisplayName: string;
    uniqueValues?: Array<string>;
    hasSearchableValues: boolean;
    isPromoted: boolean;
    isDeleted: boolean;
    isHidden: boolean;
    isDisabled: boolean;
}

type FilterMetadata = Record<string, Record<string, Record<string, FilterMetadataField>>>;

export interface ProductLineFilterRule {
    id: string;
    kind: string;
    group: string;
    field: string;
    operator: string;
    value?: unknown;
}

interface ProductLineForFiltering {
    type: string;
    metadata?: Record<string, unknown>;
}

interface SubscriptionForFiltering {
    productLines?: Array<ProductLineForFiltering>;
}

const SCHEMA_TYPE_TO_FILTER_TYPE: Record<string, string> = {
    bool: 'boolean',
    int: 'integer',
    string: 'string',
};

// displayPrefix carries the product line's own name (and, when recursing into a nested object
// field, that field's name too) so the flattened leaf's DisplayName reads as a full breadcrumb,
// e.g. 'Guides -> Enabled Restricted Resource Center Modules -> Announcements'. metadata-filter-builder.vue
// only supports a flat option list (no structural grouping), so the hierarchy has to live in the string.
function flattenMetadataFields (
    metadata: Record<string, ProductLineMetadataFieldSchema>,
    displayPrefix: Array<string>,
    parentPath: Array<string> = [],
): Record<string, FilterMetadataField> {
    const fields: Record<string, FilterMetadataField> = {};

    Object.keys(metadata).forEach((fieldName) => {
        const field = metadata[fieldName];
        const path = [...parentPath, fieldName];
        const fieldKey = path.join('.');

        if (field.type === 'object') {
            Object.assign(fields, flattenMetadataFields(field.metadata || {}, displayPrefix, path));

            return;
        }

        const filterType = SCHEMA_TYPE_TO_FILTER_TYPE[field.type];
        if (!filterType) {
            return;
        }

        fields[fieldKey] = {
            Type: filterType,
            DisplayName: [...displayPrefix, ...path].map(formatProductLineFieldName).join(' -> '),
            uniqueValues: field.enum,
            // /metadata/{kind}/{group}/schema/{field}/values only serves visitor/account metadata, so a
            // schema enum is the only source of value suggestions a product line field can offer.
            hasSearchableValues: false,
            isPromoted: false,
            isDeleted: false,
            isHidden: false,
            isDisabled: false,
        };
    });

    return fields;
}

// Converts the /productline/schema response into the {kind: {group: {field: {...}}}} shape
// metadata-filter-builder.vue expects, grouping by product line type and flattening nested
// object fields into dot-path leaf fields (the filter builder only supports leaf fields).
export function buildProductLineFilterMetadata (schemas: Array<ProductLineSchema>): FilterMetadata {
    const metadata: FilterMetadata = { [PRODUCT_LINE_FILTER_KIND]: {} };

    schemas.forEach((schema) => {
        metadata[PRODUCT_LINE_FILTER_KIND][schema.type] = flattenMetadataFields(schema.metadata, [schema.type]);
    });

    return metadata;
}

function compareValues (fieldValue: unknown, operator: string, ruleValue: unknown): boolean {
    const values = Array.isArray(ruleValue) ? ruleValue : [ruleValue];
    const isEmpty = fieldValue === undefined || fieldValue === null || fieldValue === '';

    switch (operator) {
        case '==':
            return values.some((value) => String(fieldValue) === String(value));
        case '!=':
            return !values.some((value) => String(fieldValue) === String(value));
        case 'contains':
            return values.some((value) => String(fieldValue ?? '').includes(String(value)));
        case '!contains':
            return !values.some((value) => String(fieldValue ?? '').includes(String(value)));
        case '>=':
            return Number(fieldValue) >= Number(values[0]);
        case '<=':
            return Number(fieldValue) <= Number(values[0]);
        case 'empty':
            return isEmpty;
        case '!empty':
            return !isEmpty;
        default:
            return true;
    }
}

export function evaluateProductLineFilterRule (
    rule: ProductLineFilterRule,
    sub: SubscriptionForFiltering,
): boolean {
    const productLine = (sub.productLines || []).find((pl) => pl.type === rule.group);
    const fieldValue = get(productLine, `metadata.${rule.field}`);

    return compareValues(fieldValue, rule.operator, rule.value);
}
